/**
 * SearchLoadingView — full-screen search loader for /results.
 *
 * Laid out on the same frame as the results page (ScreenBackground, header,
 * same gutters) so the hand-off is a morph, not a page swap: a glass status
 * panel on top — what the search covers (games, markets, lines, prices,
 * sportsbooks from site_config/search_catalog_stats), the step it is on, and
 * progress — then full-size glass placeholder cards where the real cards will
 * land. When the search completes, the panel shows the real counts
 * ("Found 500 bets · 7 games", "Scanned 23 markets · 1,839 candidate bets")
 * and the placeholders turn into cards in place, top to bottom (MorphReveal).
 */
import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Animated, Easing, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import NavigationHeader from './NavigationHeader';
import GradientButton from './GradientButton';
import { styles as screenStyles } from '../screens/sgpFlowStyles';
import { ScreenBackground } from './ThemeGradients';
import ResultSkeletonCard, { useShimmerLoop, useReducedMotion, ShimmerSweep } from './ResultSkeletonCard';
import { loaderMode, loaderTitle, formatElapsed, formatFoundSummary, LOADER_STEPS, loaderStepIndex, formatScanSummary } from '../utils/loadingPhase';
import { useSearchCatalogStats, formatStat } from '../hooks/useSearchCatalogStats';
import { payloadFixtureId } from '../utils/payloadFixtureId';

const WEB_GLASS = Platform.OS === 'web' ? {
  backdropFilter: 'blur(20px) saturate(150%)',
  WebkitBackdropFilter: 'blur(20px) saturate(150%)',
  boxShadow: '0 24px 60px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.10)',
} : {};

const MODE_CHIP = {
  sgp: { label: 'SGP', fg: '#DDD6FE', bg: 'rgba(139,92,246,0.18)', border: 'rgba(139,92,246,0.45)' },
  live: { label: 'LIVE', fg: '#A7F3D0', bg: 'rgba(16,185,129,0.16)', border: 'rgba(16,185,129,0.45)' },
  photo: { label: 'PHOTO', fg: '#DDD6FE', bg: 'rgba(139,92,246,0.18)', border: 'rgba(139,92,246,0.45)' },
  bets: { label: 'SEARCH', fg: 'rgba(255,255,255,0.78)', bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.14)' },
};

function PulseDot({ color, done, reduced }) {
  const pulse = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (done || reduced) { pulse.setValue(0); return undefined; }
    const loop = Animated.loop(Animated.timing(pulse, { toValue: 1, duration: 1200, easing: Easing.out(Easing.quad), useNativeDriver: false }));
    loop.start();
    return () => loop.stop();
  }, [done, reduced, pulse]);
  const ringScale = pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 2.4] });
  return (
    <View style={s.dotWrap}>
      {!done && !reduced && (
        <Animated.View style={[s.dotRing, { borderColor: color, transform: [{ scale: ringScale }] }]} />
      )}
      <View style={[s.dot, { backgroundColor: done ? '#34D399' : color }]} />
    </View>
  );
}

function ProgressTrack({ progress, shimmer, tint }) {
  const [width, setWidth] = useState(0);
  const value = useRef(new Animated.Value(progress)).current;
  useEffect(() => {
    Animated.timing(value, { toValue: progress, duration: 220, easing: Easing.out(Easing.quad), useNativeDriver: false }).start();
  }, [progress, value]);
  const fillWidth = value.interpolate({ inputRange: [0, 100], outputRange: ['0%', '100%'], extrapolate: 'clamp' });
  return (
    <View style={s.track} onLayout={(e) => setWidth(e.nativeEvent.layout.width)}>
      <Animated.View style={[s.fill, { width: fillWidth }]}>
        <LinearGradient colors={tint} start={{ x: 0, y: 0.5 }} end={{ x: 1, y: 0.5 }} style={StyleSheet.absoluteFill} />
        <ShimmerSweep progress={shimmer} width={width} band={0.25} intensity={0.45} />
      </Animated.View>
    </View>
  );
}

function StatTile({ value, label, approx }) {
  return (
    <View style={s.statTile}>
      <Text style={s.statValue} numberOfLines={1}>{`${approx ? '≈' : ''}${value}`}</Text>
      <Text style={s.statLabel} numberOfLines={1}>{label}</Text>
    </View>
  );
}

/** What the search covers — real numbers only; hidden when the stats doc is missing. */
function CatalogStats({ stats }) {
  if (!stats) return null;
  return (
    <View style={s.statsWrap}>
      <Text style={s.statsCaption}>Searching</Text>
      <View style={s.statsRow}>
        <StatTile value={formatStat(stats.games)} label="Games" />
        <StatTile value={formatStat(stats.markets)} label="Markets" />
        <StatTile value={formatStat(stats.lines)} label="Lines" />
        {Number.isFinite(stats.prices) && <StatTile value={formatStat(stats.prices)} label="Prices" approx />}
        {Number.isFinite(stats.sportsbooks) && <StatTile value={formatStat(stats.sportsbooks)} label="Sportsbooks" />}
      </View>
    </View>
  );
}

function StepList({ activeIndex, failed, accent, reduced }) {
  return (
    <View style={s.steps}>
      {LOADER_STEPS.map((label, i) => {
        const done = i < activeIndex;
        const active = i === activeIndex && !failed;
        return (
          <View key={label} style={s.stepItem}>
            {done
              ? <View style={[s.stepCheck]}><Text style={s.stepCheckText}>✓</Text></View>
              : active
                ? <PulseDot color={accent} done={false} reduced={reduced} />
                : <View style={s.stepPending} />}
            <Text style={[s.stepLabel, done && s.stepLabelDone, active && s.stepLabelActive]} numberOfLines={1}>{label}</Text>
          </View>
        );
      })}
    </View>
  );
}

export default function SearchLoadingView({
  IS_MOBILE, alreadySentToBackgroundRef, avgMs, betMode, canAddBackgroundQuery, cancelLoading,
  computeCap, errorMsg, goBack, isLiveQuery, pendingQuery, progress, progressText, responsive,
  retryFetch, routePayload, searchStatus, selectedLegs, sendToBackground, setShowBetSlipModal,
  totalHeaderHeight,
}) {
  const reduced = useReducedMotion();
  const shimmer = useShimmerLoop(!reduced);
  const mode = loaderMode({ query: pendingQuery, routePayload, isLiveQuery });
  const chip = MODE_CHIP[mode];
  const isComplete = searchStatus?.step === 'Complete' && !errorMsg;
  const hasCounts = Number.isFinite(searchStatus?.resultsFound);
  const title = isComplete && hasCounts
    ? formatFoundSummary({ bets: searchStatus.resultsFound, games: searchStatus.fixturesFound }, mode)
    : loaderTitle(mode);
  const stepText = errorMsg ? 'Search failed' : isComplete ? 'Ready' : (searchStatus?.step || progressText || 'Starting search');
  const tint = mode === 'live' ? ['#10B981', '#34D399'] : ['#8B5CF6', '#22D3EE'];
  const accent = mode === 'live' ? '#34D399' : '#A78BFA';
  // Placeholders are full card size (~1,000px on desktop), so two fill the screen.
  const skeletonCount = 2;
  const stats = useSearchCatalogStats();
  const stepIndex = isComplete ? LOADER_STEPS.length : loaderStepIndex(searchStatus?.step);
  const scanSummary = isComplete ? formatScanSummary(searchStatus || {}) : '';
  const showBackground = !errorMsg && !IS_MOBILE && canAddBackgroundQuery && !isComplete;

  const onSendToBackground = async () => {
    alreadySentToBackgroundRef.current = true; // prevent the unmount auto-convert from duplicating it
    const result = await sendToBackground(pendingQuery, {
      ...(routePayload?.league ? { league: routePayload.league } : {}),
      ...(routePayload?.sport ? { sport: routePayload.sport } : {}),
      prefer: betMode,
      fixtureId: payloadFixtureId(routePayload, pendingQuery),
    });
    if (result.success) goBack();
    else alreadySentToBackgroundRef.current = false;
  };

  return (
    <View style={s.container}>
      <ScreenBackground />
      <View style={[s.frame, {
        paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + responsive(12, 20, 32)) : responsive(12, 20, 16),
        paddingHorizontal: responsive(12, 24, Platform.OS === 'web' ? 48 : 24),
      }]}>
        <NavigationHeader />
        {/* Gutters match the results page (mainContent border + blurContent padding) so placeholders sit where cards land. */}
        <View style={[s.column, { paddingHorizontal: responsive(13, 25, 49) }]}>
          {/* Status panel */}
          <View style={[s.panel, WEB_GLASS]} accessibilityRole="progressbar" accessibilityLabel={`${title}. ${stepText}`} accessibilityValue={{ min: 0, max: 100, now: progress }}>
            <View style={s.panelTop}>
              <View style={[s.modeChip, { backgroundColor: chip.bg, borderColor: chip.border }]}>
                <Text style={[s.modeChipText, { color: chip.fg }]}>{chip.label}</Text>
              </View>
              <Text style={s.elapsed}>
                {formatElapsed(searchStatus?.elapsedMs)}
                {!isComplete && avgMs ? <Text style={s.elapsedAvg}>{`  ·  usually ~${Math.round(avgMs / 1000)}s`}</Text> : null}
              </Text>
            </View>

            <LinearGradient colors={['rgba(255,255,255,0.08)', 'rgba(255,255,255,0)']} start={{ x: 0.5, y: 0 }} end={{ x: 0.5, y: 1 }} style={s.panelSheen} pointerEvents="none" />
            <Text style={s.title} numberOfLines={2}>{title}</Text>
            {!!pendingQuery && (
              <Text style={s.query} numberOfLines={2}>{`“${pendingQuery}”`}</Text>
            )}
            {!!scanSummary && <Text style={s.scanSummary}>{scanSummary}</Text>}

            <CatalogStats stats={stats} />

            <StepList activeIndex={stepIndex} failed={!!errorMsg} accent={accent} reduced={reduced} />

            {/* Raw pipeline detail under the step list; the dot only marks a failure. */}
            <View style={s.stepRow}>
              {errorMsg ? <PulseDot color="#F87171" done={false} reduced={reduced} /> : null}
              <Text style={[s.stepText, !errorMsg && s.stepDetail, errorMsg && { color: '#FCA5A5', marginLeft: 10 }]} numberOfLines={1}>{stepText}</Text>
            </View>

            <ProgressTrack progress={Math.max(0, Math.min(100, progress || 0))} shimmer={isComplete ? null : shimmer} tint={tint} />

            {mode === 'photo' && typeof progressText === 'string' && progressText.startsWith('Found:') && (
              <View style={s.detected}>
                <Text style={s.detectedLabel}>Detected from your photo</Text>
                <Text style={s.detectedText}>{progressText.replace('Found: ', '')}</Text>
              </View>
            )}

            {errorMsg ? <Text style={s.error}>{String(errorMsg).slice(0, 300)}</Text> : null}

            {!isComplete && (
              <View style={s.actions}>
                {showBackground && (
                  <TouchableOpacity onPress={onSendToBackground} style={s.ghostButton} accessibilityRole="button">
                    <Text style={s.ghostButtonText}>Send to background</Text>
                  </TouchableOpacity>
                )}
                {errorMsg ? (
                  <GradientButton text="Retry" onPress={retryFetch} style={screenStyles.footerButton} />
                ) : null}
                <TouchableOpacity onPress={cancelLoading} style={s.textButton} accessibilityRole="button">
                  <Text style={s.textButtonText}>Cancel</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Placeholders where the cards will land */}
          {!errorMsg && (
            <View style={s.skeletons}>
              {Array.from({ length: skeletonCount }, (_, i) => (
                <ResultSkeletonCard key={i} seed={i} shimmer={shimmer} sgp={mode === 'sgp'} compact={IS_MOBILE} />
              ))}
            </View>
          )}
        </View>
      </View>

      {selectedLegs.length > 0 ? (
        <View style={s.legBar}>
          <Text style={{ color: 'rgba(255,255,255,0.9)' }}>Selected: {selectedLegs.length} / {computeCap()}</Text>
          <GradientButton text="Review BetSlip" onPress={() => setShowBetSlipModal(true)} style={screenStyles.footerButton} />
        </View>
      ) : null}
    </View>
  );
}

const s = StyleSheet.create({
  container: { flex: 1, minHeight: Platform.OS === 'web' ? '110vh' : '100%' },
  frame: { flex: 1, width: '100%' },
  column: { width: '100%', alignSelf: 'center', marginTop: 8 },
  panel: {
    width: '100%',
    borderRadius: 20,
    paddingVertical: 26,
    paddingHorizontal: 26,
    marginBottom: 20,
    minHeight: 300,
    overflow: 'hidden',
    backgroundColor: 'rgba(22,22,32,0.62)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    borderTopColor: 'rgba(255,255,255,0.24)',
  },
  panelSheen: { position: 'absolute', top: 0, left: 0, right: 0, height: 120 },
  scanSummary: { color: 'rgba(167,139,250,0.9)', fontSize: 13, fontWeight: '600', marginTop: 8 },
  statsWrap: { marginTop: 20 },
  statsCaption: { color: 'rgba(255,255,255,0.45)', fontSize: 11, fontWeight: '700', letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 8 },
  statsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  statTile: {
    flexGrow: 1,
    flexBasis: 110,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.045)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    borderTopColor: 'rgba(255,255,255,0.16)',
  },
  statValue: { color: '#fff', fontSize: 22, fontWeight: '800', letterSpacing: -0.4, fontVariant: ['tabular-nums'] },
  statLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase', marginTop: 3 },
  steps: { flexDirection: 'row', flexWrap: 'wrap', gap: 18, marginTop: 20 },
  stepItem: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  stepCheck: { width: 14, height: 14, borderRadius: 7, backgroundColor: 'rgba(52,211,153,0.22)', alignItems: 'center', justifyContent: 'center' },
  stepCheckText: { color: '#34D399', fontSize: 9, fontWeight: '900' },
  stepPending: { width: 8, height: 8, borderRadius: 4, borderWidth: 1, borderColor: 'rgba(255,255,255,0.25)', marginHorizontal: 1 },
  stepLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 13, fontWeight: '500' },
  stepLabelDone: { color: 'rgba(255,255,255,0.7)' },
  stepLabelActive: { color: '#fff', fontWeight: '700' },
  panelTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  modeChip: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, borderWidth: 1 },
  modeChipText: { fontSize: 11, fontWeight: '800', letterSpacing: 1.2 },
  elapsed: { color: 'rgba(255,255,255,0.72)', fontSize: 13, fontWeight: '600', fontVariant: ['tabular-nums'] },
  elapsedAvg: { color: 'rgba(255,255,255,0.4)', fontWeight: '500' },
  title: { color: 'rgba(255,255,255,0.97)', fontSize: 26, fontWeight: '800', letterSpacing: -0.5 },
  query: { color: 'rgba(255,255,255,0.55)', fontSize: 15, marginTop: 6 },
  stepRow: { flexDirection: 'row', alignItems: 'center', marginTop: 14, marginBottom: 10 },
  stepText: { color: 'rgba(255,255,255,0.78)', fontSize: 13, fontWeight: '500', flex: 1 },
  stepDetail: { color: 'rgba(255,255,255,0.42)', fontSize: 12 },
  dotWrap: { width: 10, height: 10, alignItems: 'center', justifyContent: 'center' },
  dot: { width: 8, height: 8, borderRadius: 4 },
  dotRing: { position: 'absolute', width: 8, height: 8, borderRadius: 4, borderWidth: 1 },
  track: { height: 4, width: '100%', borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.07)', overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 999, overflow: 'hidden' },
  detected: { marginTop: 14, padding: 12, borderRadius: 10, backgroundColor: 'rgba(139,92,246,0.12)', borderLeftWidth: 3, borderLeftColor: '#8B5CF6' },
  detectedLabel: { color: '#C4B5FD', fontSize: 12, fontWeight: '700', letterSpacing: 0.3 },
  detectedText: { color: 'rgba(255,255,255,0.9)', fontSize: 14, marginTop: 4 },
  error: { color: '#FCA5A5', fontSize: 14, marginTop: 14, lineHeight: 20 },
  actions: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10, marginTop: 16 },
  ghostButton: { paddingVertical: 8, paddingHorizontal: 14, borderRadius: 10, backgroundColor: 'rgba(139,92,246,0.14)', borderWidth: 1, borderColor: 'rgba(139,92,246,0.4)' },
  ghostButtonText: { color: '#DDD6FE', fontSize: 13, fontWeight: '600' },
  textButton: { paddingVertical: 8, paddingHorizontal: 12, borderRadius: 10 },
  textButtonText: { color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: '600' },
  skeletons: { width: '100%' },
  legBar: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    bottom: 0, left: 0, right: 0,
    borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.12)',
    paddingTop: 8, paddingBottom: 10, paddingHorizontal: 16,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    backgroundColor: '#000',
  },
});
